/**
 * Утилита для логирования ошибок API запросов
 * Отправляет детальную информацию об ошибке в систему логирования
 */

interface ApiErrorLogData {
    /** Путь API endpoint'а */
    endpoint: string;
    /** HTTP метод запроса */
    method: string;
    /** Параметры запроса */
    request: {
        url: string;
        params?: Record<string, unknown>;
        headers?: Record<string, string>;
        body?: unknown;
    };
    /** Информация об ошибке */
    error: {
        /** Текст ошибки */
        message: string;
        /** HTTP статус код */
        status?: number;
        /** Стек ошибки */
        stack?: string;
    };
    /** Ответ сервера */
    response: {
        /** Оригинальный ответ (как получен) */
        original: unknown;
        /** Попытка парсинга как JSON */
        json: unknown;
        /** Статус код ответа */
        status?: number;
        /** Заголовки ответа */
        headers?: Record<string, string>;
    };
}

/**
 * Логирует ошибку API запроса
 * @param data - Данные об ошибке
 */
export const logApiError = async (data: ApiErrorLogData): Promise<void> => {
    // Проверяем, что мы в браузере
    if (typeof window === 'undefined') {
        console.error('API Error (server-side):', data);
        return;
    }

    try {
        // Отправляем лог об ошибке
        await fetch('/api-v2/send-log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                level: 3, // Уровень ошибки
                msg: JSON.stringify({
                    error: 'API Request Error',
                    endpoint: data.endpoint,
                    method: data.method,
                    request: {
                        url: data.request.url,
                        params: data.request.params,
                        headers: data.request.headers,
                        body: data.request.body,
                    },
                    errorDetails: {
                        message: data.error.message,
                        status: data.error.status,
                        stack: data.error.stack,
                    },
                    response: {
                        original: data.response.original,
                        json: data.response.json,
                        status: data.response.status,
                        headers: data.response.headers,
                    },
                }),
                href: window.location.href,
                titlePage: document?.title,
                userAgent: navigator?.userAgent,
            }),
        });
    } catch (logError) {
        console.error('Ошибка отправки лога об API ошибке:', logError);
        console.error('Исходная API ошибка:', data);
    }
};

/**
 * Создает обработчик ошибок для React Query
 * @param endpoint - API endpoint
 * @param method - HTTP метод
 */
export const createApiErrorHandler = (endpoint: string, method: string = 'GET') => {
    return (error: unknown, variables?: unknown) => {
        console.error(`API Error [${method} ${endpoint}]:`, error);

        // Извлекаем данные из ошибки
        let responseData: unknown = null;
        let responseJson: unknown = null;
        let status: number | undefined;
        let headers: Record<string, string> | undefined;

        // Обрабатываем AxiosError
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as {
                response?: {
                    data?: unknown;
                    status?: number;
                    headers?: Record<string, string>;
                };
                config?: {
                    url?: string;
                    params?: unknown;
                    headers?: Record<string, string>;
                    data?: unknown;
                };
                message?: string;
                stack?: string;
            };

            if (axiosError.response) {
                responseData = axiosError.response.data;
                status = axiosError.response.status;
                headers = axiosError.response.headers;

                // Пытаемся распарсить JSON если это строка
                if (typeof responseData === 'string') {
                    try {
                        responseJson = JSON.parse(responseData);
                    } catch {
                        // Не удалось распарсить как JSON, оставляем как есть
                        responseJson = 'Failed to parse as JSON';
                    }
                } else {
                    responseJson = responseData;
                }
            }

            // Логируем ошибку
            void logApiError({
                endpoint,
                method,
                request: {
                    url: axiosError.config?.url || endpoint,
                    params: (axiosError.config?.params as Record<string, unknown>) || (variables as Record<string, unknown>),
                    headers: axiosError.config?.headers,
                    body: axiosError.config?.data,
                },
                error: {
                    message: axiosError.message || String(error),
                    status,
                    stack: axiosError.stack,
                },
                response: {
                    original: responseData,
                    json: responseJson,
                    status,
                    headers,
                },
            });
        } else {
            // Обрабатываем обычные ошибки
            const errorObj = error as { message?: string; stack?: string };
            void logApiError({
                endpoint,
                method,
                request: {
                    url: endpoint,
                    params: variables as Record<string, unknown> | undefined,
                },
                error: {
                    message: errorObj.message || String(error),
                    stack: errorObj.stack,
                },
                response: {
                    original: null,
                    json: null,
                },
            });
        }
    };
};

/**
 * Данные для логирования ошибки авторизации Google
 */
interface GoogleAuthErrorLogData {
    /** Тип ошибки авторизации */
    errorType: 'window_opener_null' | 'post_message_failed' | 'window_close_failed' | 'unknown';
    /** Сообщение об ошибке */
    message: string;
    /** Дополнительные детали */
    details?: {
        /** URL страницы */
        href?: string;
        /** User Agent */
        userAgent?: string;
        /** Токен авторизации (безопасно) */
        tokenExists?: boolean;
        /** Наличие window.opener */
        hasWindowOpener?: boolean;
        /** Стек ошибки */
        stack?: string;
    };
}

/**
 * Логирует ошибку авторизации Google в Telegram
 * @param data - Данные об ошибке авторизации
 */
export const logGoogleAuthError = async (data: GoogleAuthErrorLogData): Promise<void> => {
    // Проверяем, что мы в браузере
    if (typeof window === 'undefined') {
        console.error('Google Auth Error (server-side):', data);
        return;
    }

    try {
        // Отправляем лог об ошибке авторизации Google
        await fetch('/api-v2/send-log', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                level: 4, // Высокий уровень ошибки для авторизации
                msg: JSON.stringify({
                    error: 'Google OAuth Authorization Error',
                    errorType: data.errorType,
                    message: data.message,
                    details: {
                        ...data.details,
                        href: window.location.href,
                        userAgent: navigator?.userAgent,
                        timestamp: new Date().toISOString(),
                        pageTitle: document?.title,
                    },
                }),
                href: window.location.href,
                titlePage: document?.title,
                userAgent: navigator?.userAgent,
            }),
        });
    } catch (logError) {
        console.error('Ошибка отправки лога об ошибке авторизации Google:', logError);
        console.error('Исходная ошибка авторизации:', data);
    }
};

/**
 * Wrapper для axios запросов с автоматическим логированием ошибок
 * @param requestFn - Функция выполняющая запрос
 * @param endpoint - API endpoint для логирования
 * @param method - HTTP метод
 */
export const withApiErrorLogging = async <T>(
    requestFn: () => Promise<T>,
    endpoint: string,
    method: string = 'GET'
): Promise<T> => {
    try {
        return await requestFn();
    } catch (error) {
        // Создаем обработчик ошибок и вызываем его
        const errorHandler = createApiErrorHandler(endpoint, method);
        errorHandler(error);

        // Повторно бросаем ошибку для корректной обработки в React Query
        throw error;
    }
};
